import React from 'react';
import { View, Pressable, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../theme';
import Txt from './Txt';

/**
 * RN port of theme.css's .t-table. HTML tables have no RN equivalent, so this is a
 * flex layout: Table > THead/TR > TH/TD. Give matching `flex` (or width) to a column's
 * TH and TD so header and body line up. Screens drive column sizing.
 */
export function Table({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const t = useTheme();
  return (
    <View style={[{ borderWidth: 1, borderColor: t.colors.line, borderRadius: t.radii[3], overflow: 'hidden' }, style]}>
      {children}
    </View>
  );
}

/** Header row — paper-2 background, bottom border. */
export function THead({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const t = useTheme();
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: t.colors.paper2,
          borderBottomWidth: 1,
          borderBottomColor: t.colors.line,
          paddingHorizontal: 12,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/** Header cell — uppercase muted label. */
export function TH({ children, flex = 1, width, style }: { children?: React.ReactNode; flex?: number; width?: number; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={[{ flex: width ? undefined : flex, width, paddingVertical: 9, paddingRight: 12 }, style]}>
      {typeof children === 'string'
        ? <Txt size="xs" upper color="ink3" weight="medium">{children}</Txt>
        : children}
    </View>
  );
}

/** Body row — card bg (or accent-soft when selected), bottom border, optional press. */
export function TR({
  children, selected, onPress, last, style,
}: { children: React.ReactNode; selected?: boolean; onPress?: () => void; last?: boolean; style?: StyleProp<ViewStyle> }) {
  const t = useTheme();
  const inner = (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: selected ? t.colors.accentSoft : t.colors.card,
          borderBottomWidth: last ? 0 : 1,
          borderBottomColor: t.colors.line,
          paddingHorizontal: 12,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
  return onPress ? <Pressable onPress={onPress}>{inner}</Pressable> : inner;
}

/** Body cell. */
export function TD({ children, flex = 1, width, style }: { children?: React.ReactNode; flex?: number; width?: number; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={[{ flex: width ? undefined : flex, width, paddingVertical: 11, paddingRight: 12, justifyContent: 'center' }, style]}>
      {typeof children === 'string' ? <Txt size="md">{children}</Txt> : children}
    </View>
  );
}

/** Back-compat alias — old screens imported TableRow. */
export const TableRow = TR;
